import axios from "axios";
import { toast } from "sonner";
import { API_URL } from "@/runtime-env";



const apiClient = axios.create({
	baseURL: API_URL,
	headers: {
		Authorization: localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "",
	},
});

async function refreshToken(): Promise<string | null> {
	try {
		const response = await axios.post(
			`${API_URL}/auth/refresh`,
			{},
			{
				withCredentials: true,
			}
		);

		if (response.data.access_token) {
			localStorage.setItem("token", response.data.access_token);
			return response.data.access_token;
		}
		return null;
	} catch {
		localStorage.removeItem("token");
		return null;
	}
}

apiClient.interceptors.request.use(
	(config) => {
		if (config?.headers)
			config.headers.Authorization = localStorage.getItem("token") ? `Bearer ${localStorage.getItem("token")}` : "";
		return config;
	},
	(error) => {
		throw error;
	}
);

let isRefreshing = false;
let failedQueue: Array<{
	resolve: (value?: any) => void;
	reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
	for (const prom of failedQueue) {
		if (error) {
			prom.reject(error);
		} else {
			prom.resolve(token);
		}
	}

	failedQueue = [];
};

apiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const originalRequest = error.config;

		if (error.response?.status === 403) {
			setTimeout(() => {
				toast.error("Forbidden");
			}, 0);
		}

		if (error.response?.status === 401 && !originalRequest._retry) {
			if (
				originalRequest.url?.includes("/auth/refresh") ||
				originalRequest.url?.includes("/auth/login") ||
				originalRequest.url?.includes("/auth/verify")
			) {
				localStorage.removeItem("token");

				if (
					globalThis.location.pathname !== "/sign-in" &&
					globalThis.location.pathname !== "/forgot-password" &&
					globalThis.location.pathname !== "/reset-password" &&
					globalThis.location.pathname !== "/confirm-email"
				) {
					globalThis.location.href = "/sign-in";
				}
				throw error;
			}

			if (isRefreshing) {
				return new Promise((resolve, reject) => {
					failedQueue.push({ resolve, reject });
				})
					.then((token) => {
						originalRequest.headers.Authorization = `Bearer ${token}`;
						return apiClient(originalRequest);
					})
					.catch((error_) => {
						throw error_;
					});
			}

			originalRequest._retry = true;
			isRefreshing = true;

			const newToken = await refreshToken();

			if (newToken) {
				originalRequest.headers.Authorization = `Bearer ${newToken}`;
				processQueue(null, newToken);
				isRefreshing = false;
				return apiClient(originalRequest);
			} else {
				processQueue(error, null);
				isRefreshing = false;
				localStorage.removeItem("token");

				if (
					globalThis.location.pathname !== "/sign-in" &&
					globalThis.location.pathname !== "/forgot-password" &&
					globalThis.location.pathname !== "/reset-password" &&
					globalThis.location.pathname !== "/confirm-email"
				) {
					globalThis.location.href = "/sign-in";
				}
				throw error;
			}
		}

		throw error;
	}
);

export default apiClient;
